/**
 * CashTab — Cajas de los terminales (PLAN_POS_IMPLEMENTACION F5b).
 *
 * La caja llega hecha desde el terminal: esta pantalla no la abre ni la cierra.
 * Muestra el arqueo (esperado contra contado) y si el cierre ya está en la
 * contabilidad. Lo que pide atención va arriba, como avisos que filtran: cierres
 * sin cuentas, con ventas en conflicto o esperando ventas que no llegan.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ChevronLeft, ChevronRight, Eye, Landmark, BookCheck } from 'lucide-react'
import { usePermission } from '@/hooks/usePermission'
import { useToast, ToastContainer } from '@/components/Toast'
import { Button } from '@/components/Button'
import { DataTable } from '@/components/ds/DataTable'
import { Modal } from '@/components/ds/Modal'
import { SelectField } from '@/components/ds/SelectField'
import { StatusBadge } from '@/components/ds/StatusBadge'
import { POS_CASH_ACCOUNTING_STATUS } from '@/components/ds/statusCatalogs'
import { EmptyState } from '@/components/EmptyState'
import { EntityAutocomplete } from '@/components/EntityAutocomplete'
import { extractErrorMessage } from '@/lib/errorUtils'
import { formatMoneyForTenant } from '@/lib/locale'
import { fmtDateTime } from '@/lib/utils'
import { useAccounts } from '@/modules/accounting/hooks/useAccounting'
import { POS_CAPABILITIES } from '../capabilities'
import {
  usePosCashSessions, usePosCashSession, useAccountCashSession, usePosAccounts, useSavePosAccounts,
  usePosStores, type PosCashSession, type PosAccounts,
} from '../hooks/usePos'

const MOVIMIENTOS: Record<string, string> = { cash_in: 'Ingreso', withdrawal: 'Retiro', expense: 'Gasto menor' }
const METODOS: Record<string, string> = { debit: 'Débito', credit: 'Crédito', transfer: 'Transferencia', other: 'Otro' }
const CAPACIDAD = Object.fromEntries(POS_CAPABILITIES.map((c) => [c.key, c.label]))
const AVISOS: { key: 'not_configured' | 'blocked' | 'waiting_sales' | 'pending'; uno: string; varios: string }[] = [
  { key: 'not_configured', uno: '1 cierre sin cuentas configuradas', varios: 'cierres sin cuentas configuradas' },
  { key: 'blocked', uno: '1 cierre con ventas en conflicto', varios: 'cierres con ventas en conflicto' },
  { key: 'pending', uno: '1 cierre sin contabilizar', varios: 'cierres sin contabilizar' },
  { key: 'waiting_sales', uno: '1 cierre esperando ventas', varios: 'cierres esperando ventas' },
]
/** Lo que se puede reintentar desde acá: el resto se destraba solo o con el POS. */
const REINTENTABLE = new Set(['pending', 'not_configured'])

function Diferencia({ valor }: { valor: string | null }) {
  if (valor === null) return <span className="text-content-subtle">—</span>
  const n = Number(valor)
  if (n === 0) return <span className="tabular-nums text-content-muted">{formatMoneyForTenant(0)}</span>
  return (
    <span className={`tabular-nums font-medium ${n > 0 ? 'text-success-content' : 'text-danger-content'}`}
      title={n > 0 ? 'Sobrante' : 'Faltante'}>
      {n > 0 ? '+' : '−'}{formatMoneyForTenant(Math.abs(n))}
    </span>
  )
}

export default function CashTab() {
  const { can } = usePermission()
  const toast = useToast()
  const [estado, setEstado] = useState('')
  const [local, setLocal] = useState('')
  const [contable, setContable] = useState('')
  const [page, setPage] = useState(1)
  const [detalle, setDetalle] = useState<string | null>(null)
  const [cuentas, setCuentas] = useState(false)
  const { data: locales = [] } = usePosStores()
  const { data, isLoading } = usePosCashSessions({
    status: estado || undefined, store: local || undefined, accounting_status: contable || undefined, page,
  })
  const { mutate: contabilizar, isPending: contabilizando } = useAccountCashSession()
  const puedeContabilizar = can('pos.cash', 'approve')
  const cajas = data?.sessions ?? []
  const paginas = data ? Math.max(1, Math.ceil(data.count / data.page_size)) : 1

  const reintentar = (c: PosCashSession) => contabilizar(c.id, {
    onSuccess: (r) => r.accounting_status === 'accounted'
      ? toast.success(`Cierre de ${c.terminal_code} contabilizado`)
      : toast.error(r.missing_accounts.length
        ? `Faltan cuentas: ${r.missing_accounts.join(', ')}.`
        : `Sigue sin contabilizar: ${POS_CASH_ACCOUNTING_STATUS[r.accounting_status]?.label ?? r.accounting_status}.`),
    onError: (e) => toast.error(extractErrorMessage(e, 'No se pudo contabilizar el cierre.')),
  })

  return (
    <>
      {data && AVISOS.some((a) => data.pending[a.key] > 0) && (
        <div className="mb-4 flex flex-wrap gap-2" role="status">
          {AVISOS.filter((a) => data.pending[a.key] > 0).map((a) => (
            <button key={a.key} onClick={() => { setContable(a.key); setEstado('closed'); setPage(1) }}
              className="inline-flex items-center gap-2 rounded-lg border border-warning-border bg-warning-surface px-3 py-2 text-sm text-warning-content hover:opacity-90">
              <AlertTriangle size={14} />
              {data.pending[a.key] === 1 ? a.uno : `${data.pending[a.key]} ${a.varios}`}
            </button>
          ))}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-3 lg:max-w-3xl">
          <SelectField dense label="Estado" placeholder="Abiertas y cerradas" value={estado}
            onChange={(e) => { setEstado(e.target.value); setPage(1) }}
            options={[{ value: 'open', label: 'Abiertas' }, { value: 'closed', label: 'Cerradas' }]} />
          <SelectField dense label="Local" placeholder="Todos los locales" value={local}
            onChange={(e) => { setLocal(e.target.value); setPage(1) }}
            options={locales.map((s) => ({ value: s.id, label: `${s.code} · ${s.name}` }))} />
          <SelectField dense label="Contabilidad" placeholder="Todas" value={contable}
            onChange={(e) => { setContable(e.target.value); setPage(1) }}
            options={Object.entries(POS_CASH_ACCOUNTING_STATUS).map(([value, s]) => ({ value, label: s.label }))} />
        </div>
        {puedeContabilizar && (
          <Button variant="secondary" onClick={() => setCuentas(true)}>
            <Landmark size={14} /> Cuentas del cierre
          </Button>
        )}
      </div>

      <DataTable<PosCashSession>
        rows={cajas}
        rowKey={(c) => c.id}
        loading={isLoading}
        empty={<EmptyState
          title={estado || local || contable ? 'No hay cajas con ese filtro.' : 'Todavía no llegan cajas de los terminales.'}
          hint={estado || local || contable ? undefined : 'Cada caja que se abre en un terminal aparece acá cuando el equipo tiene conexión.'}
          size="sm" />}
        columns={[
          { key: 'terminal', header: 'Terminal', render: (c) => (
            <span><span className="font-mono">{c.terminal_code}</span>
              <span className="block text-xs text-content-subtle">{c.store_name}</span></span>
          ) },
          { key: 'operator', header: 'Cajero', render: (c) => c.operator },
          { key: 'when', header: 'Apertura · cierre', render: (c) => (
            <span className="text-sm">{fmtDateTime(c.opened_at)}
              <span className="block text-xs text-content-subtle">{c.closed_at ? fmtDateTime(c.closed_at) : 'Abierta'}</span></span>
          ) },
          { key: 'expected', header: 'Esperado', numeric: true, render: (c) => formatMoneyForTenant(c.expected_amount) },
          { key: 'declared', header: 'Contado', numeric: true,
            render: (c) => c.declared_amount !== null ? formatMoneyForTenant(c.declared_amount) : '—' },
          { key: 'difference', header: 'Diferencia', numeric: true, render: (c) => <Diferencia valor={c.difference} /> },
          { key: 'accounting', header: 'Contabilidad', render: (c) => c.status === 'open'
            ? <span className="text-sm text-content-subtle">Al cerrar</span>
            : (
              <div>
                <StatusBadge status={c.accounting_status} catalog={POS_CASH_ACCOUNTING_STATUS} />
                {c.voucher_number && <span className="mt-1 block text-xs font-mono text-content-subtle">{c.voucher_number}</span>}
              </div>
            ) },
          { key: 'actions', header: '', render: (c) => (
            <div className="flex justify-end gap-1">
              {puedeContabilizar && c.status === 'closed' && REINTENTABLE.has(c.accounting_status) && (
                <button onClick={() => reintentar(c)} disabled={contabilizando} title="Contabilizar el cierre"
                  aria-label={`Contabilizar el cierre de ${c.terminal_code}`}
                  className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-content disabled:opacity-50">
                  <BookCheck size={14} />
                </button>
              )}
              <button onClick={() => setDetalle(c.id)} title="Ver el detalle" aria-label={`Ver la caja de ${c.terminal_code}`}
                className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-content">
                <Eye size={14} />
              </button>
            </div>
          ) },
        ]}
      />

      {paginas > 1 && (
        <div className="mt-4 flex items-center justify-end gap-3 text-sm text-content-muted">
          <span>Página {page} de {paginas}</span>
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Página anterior">
            <ChevronLeft size={14} />
          </Button>
          <Button variant="secondary" size="sm" disabled={page >= paginas} onClick={() => setPage(page + 1)} aria-label="Página siguiente">
            <ChevronRight size={14} />
          </Button>
        </div>
      )}

      {detalle && <CashDetailModal id={detalle} onClose={() => setDetalle(null)} />}
      {cuentas && (
        <AccountsModal onClose={() => setCuentas(false)}
          onSaved={() => { toast.success('Cuentas del cierre guardadas'); setCuentas(false) }} />
      )}
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}

// ─── Detalle ─────────────────────────────────────────────────────────────────

function Fila({ label, valor, fuerte }: { label: string; valor: React.ReactNode; fuerte?: boolean }) {
  return (
    <div className={`flex justify-between gap-4 py-1 text-sm ${fuerte ? 'border-t border-divider pt-2 font-medium text-content' : 'text-content-muted'}`}>
      <span>{label}</span><span className="tabular-nums">{valor}</span>
    </div>
  )
}

function CashDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: c, isLoading } = usePosCashSession(id)
  const otros = Object.entries(c?.other_payments ?? {})

  return (
    <Modal open onClose={onClose} size="lg"
      title={c ? `Caja de ${c.terminal_code} · ${c.store_name}` : 'Caja'}
      subtitle={c ? `${c.operator} · ${fmtDateTime(c.opened_at)}${c.closed_at ? ` – ${fmtDateTime(c.closed_at)}` : ' · abierta'}` : undefined}
      actions={<Button variant="secondary" onClick={onClose}>Cerrar</Button>}>
      {isLoading || !c ? <p className="text-sm text-content-muted">Cargando…</p> : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <h4 className="mb-1 text-sm font-semibold text-content">Arqueo</h4>
              <Fila label="Fondo inicial" valor={formatMoneyForTenant(c.opening_amount)} />
              <Fila label="Ventas en efectivo" valor={formatMoneyForTenant(c.cash_sales)} />
              <Fila label="Devoluciones en efectivo" valor={`− ${formatMoneyForTenant(c.cash_refunds)}`} />
              <Fila label="Ingresos" valor={formatMoneyForTenant(c.cash_in)} />
              <Fila label="Retiros" valor={`− ${formatMoneyForTenant(c.withdrawals)}`} />
              <Fila label="Gastos menores" valor={`− ${formatMoneyForTenant(c.expenses)}`} />
              <Fila label="Esperado" valor={formatMoneyForTenant(c.expected_amount)} fuerte />
              <Fila label="Contado" valor={c.declared_amount !== null ? formatMoneyForTenant(c.declared_amount) : '—'} />
              <Fila label="Diferencia" valor={<Diferencia valor={c.difference} />} fuerte />
            </div>
            <div>
              <h4 className="mb-1 text-sm font-semibold text-content">Otros medios de pago</h4>
              {otros.length === 0
                ? <p className="text-sm text-content-muted">Solo efectivo.</p>
                : otros.map(([m, v]) => <Fila key={m} label={METODOS[m] ?? m} valor={formatMoneyForTenant(v)} />)}
              <p className="mt-1 text-xs text-content-subtle">No entran al asiento del cierre: se contabilizan como cobros.</p>
              <h4 className="mb-1 mt-4 text-sm font-semibold text-content">Contabilidad</h4>
              {c.status === 'open'
                ? <p className="text-sm text-content-muted">Se contabiliza al cerrar.</p>
                : (
                  <div className="space-y-1 text-sm">
                    <StatusBadge status={c.accounting_status} catalog={POS_CASH_ACCOUNTING_STATUS} />
                    {c.voucher_id && (
                      <Link to={`/accounting/vouchers?highlight=${c.voucher_id}`} className="block text-primary hover:underline">
                        Comprobante {c.voucher_number || ''}
                      </Link>
                    )}
                    {c.accounting_status === 'waiting_sales' && (
                      <p className="text-content-muted">Llegaron {c.sales_received} de {c.sales_reported} ventas. Se contabiliza sola al llegar la última.</p>
                    )}
                    {c.accounting_status === 'blocked' && (
                      <p className="text-content-muted">Una venta o nota de crédito en conflicto movió efectivo: ese efectivo no tiene un cobro ni una nota contra la cual contabilizarse.</p>
                    )}
                    {c.missing_accounts.length > 0 && (
                      <p className="text-content-muted">Faltan cuentas: {c.missing_accounts.join(', ')}.</p>
                    )}
                  </div>
                )}
            </div>
          </div>

          {c.warnings.length > 0 && (
            <ul className="space-y-1 rounded-lg border border-warning-border bg-warning-surface p-3 text-sm text-warning-content">
              {c.warnings.map((w, i) => <li key={`${w.code}-${i}`}>{w.message}</li>)}
            </ul>
          )}

          <div>
            <h4 className="mb-2 text-sm font-semibold text-content">Movimientos de efectivo</h4>
            <DataTable<NonNullable<PosCashSession['movements']>[number]>
              rows={c.movements ?? []}
              rowKey={(m) => m.id}
              empty={<EmptyState title="Sin movimientos." size="sm" />}
              columns={[
                { key: 'when', header: 'Hora', render: (m) => fmtDateTime(m.occurred_at) },
                { key: 'kind', header: 'Tipo', render: (m) => MOVIMIENTOS[m.kind] },
                { key: 'reason', header: 'Motivo', render: (m) => (
                  <span>{m.reason}
                    <span className="block text-xs text-content-subtle">
                      {m.operator}{m.authorized_by ? ` · autorizó ${m.authorized_by}` : ''}
                    </span>
                  </span>
                ) },
                { key: 'amount', header: 'Monto', numeric: true, render: (m) => formatMoneyForTenant(m.amount) },
              ]}
            />
          </div>

          {(c.authorizations ?? []).length > 0 && (
            <div>
              <h4 className="mb-2 text-sm font-semibold text-content">Autorizaciones</h4>
              <ul className="space-y-1 text-sm">
                {(c.authorizations ?? []).map((a, i) => (
                  <li key={i} className="text-content-muted">
                    <span className="text-content">{CAPACIDAD[a.capability] ?? a.capability}</span>
                    {a.folio ? ` (boleta N° ${a.folio})` : ''}: {a.authorized_by} autorizó a {a.operator}
                    {a.detail ? ` · ${a.detail}` : ''} · {fmtDateTime(a.occurred_at)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

// ─── Cuentas del cierre ──────────────────────────────────────────────────────

function AccountsModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { data } = usePosAccounts()
  return data
    ? <AccountsForm initial={data} onClose={onClose} onSaved={onSaved} />
    : (
      <Modal open onClose={onClose} title="Cuentas del cierre de caja" size="lg">
        <p className="text-sm text-content-muted">Cargando…</p>
      </Modal>
    )
}

/** Fuera de `AccountsForm`: definido adentro se volvería a montar en cada tecla. */
function Selector({ label, value, onChange, opciones }: {
  label: string; value: string; onChange: (v: string) => void; opciones: { id: string; label: string }[]
}) {
  return (
    <EntityAutocomplete aria-label={label} options={opciones} value={value} onChange={onChange}
      placeholder="Seleccionar la cuenta" allowClear clearLabel="Sin cuenta" />
  )
}

function AccountsForm({ initial, onClose, onSaved }: { initial: PosAccounts; onClose: () => void; onSaved: () => void }) {
  const { data: plan } = useAccounts({ is_imputable: 'true', is_active: 'true' })
  const { mutate, isPending } = useSavePosAccounts()
  const [error, setError] = useState<string | null>(null)
  const [retiros, setRetiros] = useState(initial.withdrawal_account?.id ?? '')
  const [gastos, setGastos] = useState(initial.petty_expense_account?.id ?? '')
  const [locales, setLocales] = useState(initial.stores.map((s) => ({
    id: s.id, code: s.code, name: s.name,
    cash_account: s.cash_account?.id ?? '', cash_difference_account: s.cash_difference_account?.id ?? '',
  })))
  const opciones = useMemo(
    () => (plan?.accounts ?? []).map((a) => ({ id: a.id, label: `${a.code} — ${a.name}` })), [plan],
  )
  const cambiar = (i: number, campo: 'cash_account' | 'cash_difference_account', v: string) =>
    setLocales(locales.map((s, j) => (j === i ? { ...s, [campo]: v } : s)))

  const guardar = () => {
    setError(null)
    mutate({
      withdrawal_account: retiros || null, petty_expense_account: gastos || null,
      stores: locales.map((s) => ({
        id: s.id, cash_account: s.cash_account || null, cash_difference_account: s.cash_difference_account || null,
      })),
    }, { onSuccess: onSaved, onError: (e) => setError(extractErrorMessage(e, 'No se pudieron guardar las cuentas.')) })
  }

  return (
    <Modal open onClose={onClose} title="Cuentas del cierre de caja" size="xl" dismissOnScrimClick={false}
      subtitle="Sin estas cuentas la caja se cierra igual y queda «Faltan cuentas»; se contabiliza después, desde la lista."
      actions={<>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button onClick={guardar} isPending={isPending}>Guardar</Button>
      </>}>
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <span className="cl-label mb-1.5 block">Destino de los retiros</span>
            <Selector opciones={opciones} label="Destino de los retiros" value={retiros} onChange={setRetiros} />
            <p className="mt-1 text-xs text-content-subtle">Bóveda o banco. Un ingreso a la caja sale de esta misma cuenta.</p>
          </div>
          <div>
            <span className="cl-label mb-1.5 block">Gastos menores</span>
            <Selector opciones={opciones} label="Gastos menores" value={gastos} onChange={setGastos} />
          </div>
        </div>
        <div>
          <h4 className="mb-2 text-sm font-semibold text-content">Por local</h4>
          {locales.length === 0 ? <p className="text-sm text-content-muted">No hay locales.</p> : (
            <div className="space-y-3">
              <div className="hidden gap-3 sm:grid sm:grid-cols-[10rem_1fr_1fr]">
                <span />
                <span className="cl-label">Caja del local</span>
                <span className="cl-label">Diferencias de caja</span>
              </div>
              {locales.map((s, i) => (
                <div key={s.id} className="grid grid-cols-1 gap-3 border-t border-divider pt-3 sm:grid-cols-[10rem_1fr_1fr] sm:items-center">
                  <span className="text-sm text-content"><span className="font-mono">{s.code}</span>
                    <span className="block text-xs text-content-subtle">{s.name}</span></span>
                  <Selector opciones={opciones} label={`Caja del local ${s.code}`} value={s.cash_account} onChange={(v) => cambiar(i, 'cash_account', v)} />
                  <Selector opciones={opciones} label={`Diferencias de caja ${s.code}`} value={s.cash_difference_account}
                    onChange={(v) => cambiar(i, 'cash_difference_account', v)} />
                </div>
              ))}
              <p className="text-xs text-content-subtle">
                La caja del local es un activo. Diferencias de caja es una sola cuenta de resultado para sobrantes y faltantes.
              </p>
            </div>
          )}
        </div>
        {error && <p className="text-sm text-danger-content">{error}</p>}
      </div>
    </Modal>
  )
}
